/**
 * Single source of truth for where the developer-tools toolbar floats.
 *
 * The `Position` type used to be declared three times (DeveloperTools,
 * DeveloperToolsDialog, PositionData) and the stored value was cast straight
 * from localStorage without validation, so a stale key could resolve to no
 * placement classes at all. Both problems live here now.
 */

export type DevToolsPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';

/**
 * Bottom-left by default: `top-center` sat directly on top of the navbar's global
 * search input, and bottom-right is taken by the support widget.
 */
export const DEFAULT_DEV_TOOLS_POSITION: DevToolsPosition = 'top-center';

const STORAGE_KEY = 'developer-tools-position';

export const DEV_TOOLS_POSITION_OPTIONS: ReadonlyArray<{ value: DevToolsPosition; label: string }> = [
  { value: 'top-left', label: 'Superior esquerda' },
  { value: 'top-center', label: 'Superior centro' },
  { value: 'top-right', label: 'Superior direita' },
  { value: 'bottom-left', label: 'Inferior esquerda' },
  { value: 'bottom-center', label: 'Inferior centro' },
  { value: 'bottom-right', label: 'Inferior direita' },
];

const POSITION_CLASSES: Record<DevToolsPosition, string> = {
  'top-left': 'top-4 left-4',
  'top-center': 'top-4 left-1/2 -translate-x-1/2',
  'top-right': 'top-4 right-4',
  'bottom-left': 'bottom-4 left-4',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-4 right-4',
};

const isDevToolsPosition = (value: unknown): value is DevToolsPosition => DEV_TOOLS_POSITION_OPTIONS.some((option) => option.value === value);

export const getPositionClasses = (position: DevToolsPosition): string => POSITION_CLASSES[position] ?? POSITION_CLASSES[DEFAULT_DEV_TOOLS_POSITION];

/**
 * Open the menu away from the screen edge the toolbar is docked to. Radix
 * flips on collision anyway, but starting on the correct side avoids a
 * visible reposition on first open.
 */
export const getMenuPlacement = (position: DevToolsPosition): { side: 'top' | 'bottom'; align: 'start' | 'center' | 'end' } => ({
  side: position.startsWith('top') ? 'bottom' : 'top',
  align: position.endsWith('right') ? 'end' : position.endsWith('left') ? 'start' : 'center',
});

/**
 * Read on first render (lazy `useState` initializer) rather than in an effect —
 * an effect made the toolbar paint at the default position and then jump to the
 * stored one on every page load.
 */
export const readStoredPosition = (): DevToolsPosition => {
  if (typeof window === 'undefined') return DEFAULT_DEV_TOOLS_POSITION;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isDevToolsPosition(stored) ? stored : DEFAULT_DEV_TOOLS_POSITION;
  } catch {
    // localStorage throws in Safari private mode / blocked-cookie contexts.
    return DEFAULT_DEV_TOOLS_POSITION;
  }
};

export const writeStoredPosition = (position: DevToolsPosition): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, position);
  } catch {
    // Persisting placement is a convenience; never break the toolbar over it.
  }
};
